'use client';
import { useState } from 'react';
import { useResource } from '@/lib/use-resource';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell, ReferenceLine,
} from 'recharts';

const PERIODS = ['1M', '3M', '6M', '1Y', '2Y', '5Y'] as const;
const PILLAR_LABELS: Record<string, string> = {
  quality: 'Quality',
  profitability: 'Profitability', growth: 'Growth', safety: 'Safety', shareholderReturns: 'Shareholder Returns',
  valuation: 'Valuation', momentum: 'Momentum',
};
const PILLAR_COLORS: Record<string, string> = {
  quality: '#60a5fa',
  profitability: '#34d399', growth: '#818cf8', safety: '#fbbf24', shareholderReturns: '#f472b6',
  valuation: '#4ade80', momentum: '#c084fc',
};

type ViewMode = 'performance' | 'attribution';

interface DataPoint { date: string; value: number; }
interface StockEntry { symbol: string; name: string; pillarScore: number; return: number | null; }
interface PerfData { basket: DataPoint[]; spy: DataPoint[]; stocks: StockEntry[]; }

interface Attribution {
  epsGrowth: number | null;
  peExpansion: number | null;
  totalReturn: number | null;
  type: 'F' | 'M' | 'mixed' | 'unknown';
}

const TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  F: { label: 'F', color: '#34d399' },
  M: { label: 'M', color: '#fb923c' },
  mixed: { label: '?', color: '#64748b' },
  unknown: { label: '?', color: '#64748b' },
};

interface Props {
  pillar: string;
  onBack: () => void;
  onStockSelect?: (symbol: string) => void;
}

export default function PillarPerformanceChart({ pillar, onBack, onStockSelect }: Props) {
  const [period, setPeriod] = useState<string>('1Y');
  const [viewMode, setViewMode] = useState<ViewMode>('performance');

  // useResource gives per-URL cache + dedup automatically. Switching pillar
  // or period uses the cache if previously seen.
  const { data, loading } = useResource<PerfData>(
    `/api/pillar-performance?pillar=${pillar}&period=${period}`,
  );

  // Attribution fires only when data + stocks are available. URL embeds the
  // symbol list so cache key is per-(pillar,period) automatically.
  const attrUrl = data?.stocks && data.stocks.length > 0
    ? `/api/stock-attribution?symbols=${data.stocks.map((s) => s.symbol).join(',')}&period=${period}`
    : null;
  const { data: attributionRaw, loading: attrLoading } = useResource<Record<string, Attribution>>(attrUrl);
  const attributionData = attributionRaw ?? {};

  const chartData = data?.basket ? data.basket.map((b, i) => ({
    date: b.date,
    basket: b.value,
    spy: data.spy?.[i]?.value ?? null,
  })) : [];

  const basketReturn = data?.basket && data.basket.length > 1
    ? Math.round((data.basket[data.basket.length - 1].value - 100) * 100) / 100
    : null;
  const spyReturn = data?.spy && data.spy.length > 1
    ? Math.round((data.spy[data.spy.length - 1].value - 100) * 100) / 100
    : null;

  const label = PILLAR_LABELS[pillar] || pillar;
  const color = PILLAR_COLORS[pillar] || '#60a5fa';

  // Build attribution bar chart data
  const clamp = (v: number) => Math.max(-500, Math.min(500, v));
  const attrBarData = (data?.stocks ?? [])
    .map(s => {
      const a = attributionData[s.symbol];
      if (!a) return null;
      const isUnknown = a.type === 'unknown';
      return {
        symbol: s.symbol,
        totalReturn: a.totalReturn != null ? clamp(a.totalReturn) : 0,
        epsGrowth: !isUnknown && a.epsGrowth != null ? clamp(a.epsGrowth) : 0,
        peExpansion: !isUnknown && a.peExpansion != null ? clamp(a.peExpansion) : 0,
        unknownReturn: isUnknown ? (a.totalReturn != null ? clamp(a.totalReturn) : 0) : 0,
        type: a.type,
        rawTotalReturn: a.totalReturn,
        rawEpsGrowth: a.epsGrowth,
        rawPeExpansion: a.peExpansion,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b!.totalReturn - a!.totalReturn);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ background: '#0a0c10' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-[#64748b] hover:text-[#e2e8f0] transition-colors cursor-pointer text-sm flex items-center gap-1.5"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h2 className="text-[#e2e8f0] text-lg font-semibold">
            {label} <span className="text-[#64748b] font-normal">— &gt;1.75 Std Dev Basket vs SPY</span>
          </h2>
        </div>
        <div className="flex items-center gap-4">
          {/* View mode toggle */}
          <div className="flex bg-[#1e293b]/60 rounded-md p-0.5">
            {(['performance', 'attribution'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1 text-xs font-semibold rounded transition-colors cursor-pointer ${
                  viewMode === mode
                    ? 'bg-[#1e293b] text-white'
                    : 'text-[#64748b] hover:text-[#94a3b8]'
                }`}
              >
                {mode === 'performance' ? 'Performance' : 'Attribution'}
              </button>
            ))}
          </div>
          {/* Period buttons */}
          <div className="flex gap-1">
            {PERIODS.map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 text-xs font-semibold rounded transition-colors cursor-pointer ${
                  period === p
                    ? 'bg-[#1e293b] text-white'
                    : 'text-[#64748b] hover:text-[#94a3b8] hover:bg-[#1e293b]/50'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-3" />
            <div className="text-[#94a3b8] text-sm">Loading performance data...</div>
          </div>
        </div>
      ) : data ? (
        <div className="flex-1 flex flex-col px-6 pb-4 overflow-hidden">
          {viewMode === 'performance' ? (
            <>
              {/* Return summary */}
              <div className="flex items-center gap-6 mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-0.5 inline-block rounded" style={{ backgroundColor: color }} />
                  <span className="text-sm text-[#94a3b8]">{label} Basket</span>
                  <span className={`text-base font-bold tabular-nums ${
                    basketReturn != null && basketReturn >= 0 ? 'text-emerald-400' : 'text-red-400'
                  }`} style={{ fontFamily: 'var(--font-mono)' }}>
                    {basketReturn != null ? `${basketReturn > 0 ? '+' : ''}${basketReturn}%` : '—'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-0.5 bg-[#94a3b8] inline-block rounded" />
                  <span className="text-sm text-[#94a3b8]">SPY</span>
                  <span className={`text-base font-bold tabular-nums ${
                    spyReturn != null && spyReturn >= 0 ? 'text-emerald-400' : 'text-red-400'
                  }`} style={{ fontFamily: 'var(--font-mono)' }}>
                    {spyReturn != null ? `${spyReturn > 0 ? '+' : ''}${spyReturn}%` : '—'}
                  </span>
                </div>
              </div>

              {/* Line Chart */}
              <div className="flex-1" style={{ minHeight: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#475569' }}
                      tickFormatter={(d: string) => d.slice(5)}
                      interval="preserveStartEnd"
                      stroke="#1e293b"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#475569' }}
                      stroke="#1e293b"
                      domain={['auto', 'auto']}
                      tickFormatter={(v: number) => v.toFixed(0)}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 12 }}
                      labelStyle={{ color: '#94a3b8', fontSize: 11 }}
                      formatter={(value: unknown, name: unknown) => [
                        `${Number(value).toFixed(1)}`,
                        name === 'basket' ? `${label} Basket` : 'SPY',
                      ]}
                    />
                    <Line type="monotone" dataKey="basket" stroke={color} dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="spy" stroke="#94a3b8" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <>
              {/* Attribution Bar Chart */}
              {attrLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-3" />
                    <div className="text-[#94a3b8] text-sm">Loading attribution data...</div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Legend */}
                  <div className="flex items-center gap-5 mb-3">
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#60a5fa' }} />
                      <span className="text-xs text-[#94a3b8]">EPS Growth</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#fb923c' }} />
                      <span className="text-xs text-[#94a3b8]">PE Expansion</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-3 border-l-2 inline-block" style={{ borderColor: '#e2e8f0' }} />
                      <span className="text-xs text-[#94a3b8]">Total Return</span>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={attrBarData}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11, fill: '#475569' }}
                          stroke="#1e293b"
                          tickFormatter={(v: number) => `${v}%`}
                        />
                        <YAxis
                          type="category"
                          dataKey="symbol"
                          tick={{ fontSize: 11, fill: '#94a3b8' }}
                          stroke="#1e293b"
                          width={55}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#0f172a',
                            border: '1px solid #1e293b',
                            borderRadius: 6,
                            fontSize: 12,
                          }}
                          labelStyle={{ color: '#e2e8f0', fontSize: 12, fontWeight: 600 }}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          formatter={(value: any, name: any) => {
                            const v = Number(value);
                            if (v === 0 && name === 'unknownReturn') return [null, null];
                            const labels: Record<string, string> = {
                              epsGrowth: 'EPS Growth',
                              peExpansion: 'PE Expansion',
                              unknownReturn: 'Total Return',
                            };
                            return [`${v > 0 ? '+' : ''}${v.toFixed(1)}%`, labels[name] || name];
                          }}
                          labelFormatter={(sym) => {
                            const item = attrBarData.find(d => d!.symbol === sym);
                            if (!item) return sym;
                            const cfg = TYPE_CONFIG[item.type];
                            return `${sym}  [${cfg.label}]  Total: ${item.rawTotalReturn != null ? `${item.rawTotalReturn > 0 ? '+' : ''}${item.rawTotalReturn}%` : '—'}`;
                          }}
                        />
                        <ReferenceLine x={0} stroke="#475569" strokeWidth={1} />
                        <Bar dataKey="epsGrowth" stackId="a" fill="#60a5fa" radius={0} />
                        <Bar dataKey="peExpansion" stackId="a" fill="#fb923c" radius={0} />
                        <Bar dataKey="unknownReturn" stackId="a" fill="#475569" radius={0}>
                          {attrBarData.map((d, i) => (
                            <Cell key={i} fill={d!.type === 'unknown' ? '#475569' : 'transparent'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </>
          )}

          {/* Stock list at bottom */}
          <div className="mt-4 border-t border-[#1e293b] pt-3">
            <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-2 font-semibold">
              Stocks &gt;1.75 Std Dev — {label}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {(data?.stocks ?? []).map(s => {
                const attr = attributionData[s.symbol];
                const cfg = attr ? TYPE_CONFIG[attr.type] : null;
                return (
                  <div key={s.symbol} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white/5 rounded px-1 -mx-1 transition-colors"
                    onClick={() => onStockSelect?.(s.symbol)}>
                    <span className="text-[#60a5fa] hover:text-white font-medium w-12 transition-colors">{s.symbol}</span>
                    {cfg && (
                      <span
                        className="text-[9px] font-bold rounded px-1 py-px leading-none"
                        style={{ backgroundColor: cfg.color + '22', color: cfg.color, border: `1px solid ${cfg.color}44` }}
                      >
                        {cfg.label}
                      </span>
                    )}
                    <span className="text-[#64748b] tabular-nums w-8 text-right" style={{ fontFamily: 'var(--font-mono)' }}>
                      {s.pillarScore.toFixed(1)}
                    </span>
                    <span className={`tabular-nums w-14 text-right font-medium ${
                      s.return != null && s.return >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`} style={{ fontFamily: 'var(--font-mono)' }}>
                      {s.return != null ? `${s.return > 0 ? '+' : ''}${s.return}%` : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-[#64748b] text-sm">No data available</div>
        </div>
      )}
    </div>
  );
}
